import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { authApi } from '@/lib/api/endpoints';
import { routes } from '@/lib/routes';
import { AuthCard } from './components/AuthCard';
import { validatePassword } from './validation';
import type { TranslationKey } from '@/i18n';

const ResetPasswordPage = () => {
  const { t, locale } = useI18n();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token')?.trim() ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<TranslationKey | undefined>();
  const [isPending, setIsPending] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) {
      notify(t('auth.verify.invalid'), 'error');
      return;
    }
    const nextError =
      validatePassword(password) || (password !== confirm ? 'auth.error.passwordMatch' : undefined);
    setError(nextError);
    if (nextError) return;

    setIsPending(true);
    try {
      await authApi.confirmPasswordReset(token, password, locale);
      notify(t('auth.reset.saved'), 'success');
      navigate(routes.login);
    } catch {
      notify(t('auth.reset.failed'), 'error');
    } finally {
      setIsPending(false);
    }
  };

  return (
    <AuthCard title={t('auth.reset.title')} description={t('auth.reset.text')} showTabs={false}>
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="auth-field">
          <label htmlFor="reset-password">{t('auth.password')}</label>
          <input
            id="reset-password"
            type={showPassword ? 'text' : 'password'}
            maxLength={128}
            autoComplete="new-password"
            value={password}
            placeholder={t('auth.passwordPlaceholder')}
            aria-invalid={Boolean(error)}
            onChange={(event) => setPassword(event.target.value)}
          />
          {error && <span className="field-error">{t(error)}</span>}
        </div>

        <div className="auth-field">
          <label htmlFor="reset-confirm">{t('auth.invite.confirm')}</label>
          <input
            id="reset-confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            maxLength={128}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </div>
        <button type="button" className="auth-link" onClick={() => setShowPassword(!showPassword)}>
          {t(showPassword ? 'auth.password.hide' : 'auth.password.show')}
        </button>
        <p className="auth-note">
          {t('auth.error.password')} {t('auth.password.rulesHint')}
        </p>
        <button type="submit" className="auth-primary" disabled={isPending || !token}>
          {isPending ? t('common.loading') : t('auth.reset.save')}
        </button>

        <button type="button" className="auth-link" onClick={() => navigate(routes.login)}>
          {t('auth.backToLogin')}
        </button>
      </form>
    </AuthCard>
  );
};

export default ResetPasswordPage;
